/* eslint-disable no-useless-escape */
/* eslint-disable no-restricted-globals */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useRef } from "react";
import slugify from "slugify";
import { PulseLoader } from "react-spinners";
import { APIContext } from "../../api/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import HelmetLayout from "../../components/HelmetLayout";
import Creatable from "react-select/creatable";
import {
  ArrowLeft,
  Trash2,
  Save,
  Upload,
  Image as ImageIcon,
  Layers,
} from "lucide-react";

export default function EditTemplate({ isNew }) {
  const navigate = useNavigate();
  const { templateID } = useParams();
  const { getTemplate, deleteTemplate, createNewTemplate, updateTemplate } =
    useContext(APIContext);

  const [loading, setLoading] = useState(!isNew);
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateJSON, setTemplateJSON] = useState("");
  const [templateThumbnail, setTemplateThumbnail] = useState("");
  const [templateThumbnailFile, setTemplateThumbnailFile] = useState(null);
  const [templateMeta, setTemplateMeta] = useState({});
  const [isTemplateDeleting, setIsTemplateDeleting] = useState(false);
  const [isTemplateUpdating, setIsTemplateUpdating] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const thumbnailRef = useRef(null);

  useEffect(() => {
    if (!isNew) {
      getTemplate(templateID)
        .then((templatedata) => {
          if (templatedata) {
            setTemplateName(templatedata.template_name || "");
            setTemplateDescription(templatedata.template_description || "");
            setTemplateCategory(templatedata.template_category || "");
            setTemplateJSON(
              typeof templatedata.template_json === "string"
                ? templatedata.template_json
                : JSON.stringify(templatedata.template_json, null, 2)
            );
            setTemplateThumbnail(templatedata.template_thumbnail || "");
            setTemplateMeta(templatedata.template_meta || {});
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error("Failed to load template data");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isNew, templateID]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (isTemplateUpdating) return;

    if (!templateName.trim()) {
      toast.error("Template name is required.");
      return;
    }

    setIsTemplateUpdating(true);
    try {
      if (isNew) {
        await createNewTemplate(
          templateName,
          templateDescription,
          templateThumbnailFile,
          templateCategory,
          templateMeta,
          templateJSON
        );
        toast.success("Template created successfully!");
        navigate("/templates");
      } else {
        await updateTemplate(
          templateID,
          templateName,
          templateDescription,
          templateThumbnailFile,
          templateCategory,
          templateMeta,
          templateJSON
        );
        toast.success("Template updated successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to save template");
    } finally {
      setIsTemplateUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (isTemplateDeleting) return;
    setIsTemplateDeleting(true);
    try {
      await deleteTemplate(templateID);
      toast.success("Template deleted successfully!");
      navigate("/templates");
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to delete template");
      setIsTemplateDeleting(false);
    }
  };

  return (
    <>
      <HelmetLayout
        title={isNew ? "New Template" : `${templateName || "Edit"} - Template`}
      />

      <div className="space-y-6 pb-16 max-w-4xl">
        {/* Navigation & Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link target="_blank" rel="noopener noreferrer"
              to="/templates"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mb-2"
            >
              <ArrowLeft size={14} />
              Back to Templates
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Layers className="text-[#623ce7]" size={24} />
              {isNew ? "Create New Template" : `Edit Template: ${templateName}`}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {isNew
                ? "Configure metadata, category, and JSON structure for a new page template."
                : `Update metadata, visual assets, and JSON content for template ${templateID}.`}
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            {!isNew && (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                className="px-3.5 py-2 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={isTemplateUpdating}
              className="px-4 py-2 text-xs font-semibold text-white bg-[#623ce7] hover:bg-[#5030bc] rounded-lg transition-colors shadow-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              {isTemplateUpdating ? (
                <PulseLoader size={6} color="#ffffff" />
              ) : (
                <>
                  <Save size={14} />
                  {isNew ? "Create Template" : "Save Changes"}
                </>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center shadow-sm">
            <PulseLoader size={10} color="#623ce7" />
            <p className="text-xs text-slate-400 mt-3">Loading template...</p>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            {/* General Info Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
              <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
                General Information
              </h2>

              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Template Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Modern SaaS Landing Page"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Category
                </label>
                <select
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all capitalize"
                >
                  <option value="">Select a category...</option>
                  <option value="landing">Landing Page</option>
                  <option value="portfolio">Portfolio</option>
                  <option value="blog">Blog</option>
                  <option value="pricing">Pricing</option>
                  <option value="contact">Contact Us</option>
                  <option value="about">About Us</option>
                  <option value="privacy">Privacy Policy</option>
                  <option value="term">Terms of Service</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Detailed description of what this template is designed for..."
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all resize-y"
                />
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Keywords & Tags
                </label>
                <Creatable
                  isMulti
                  placeholder="Type keyword and press Enter..."
                  options={[]}
                  value={templateMeta?.keyword || []}
                  onChange={(selectedOptions) => {
                    setTemplateMeta({
                      ...(templateMeta || {}),
                      keyword: selectedOptions,
                    });
                  }}
                  onCreateOption={(inputValue) => {
                    const slug = slugify(inputValue, {
                      trim: false,
                      strict: true,
                      lower: true,
                    });
                    const newOption = { value: slug, label: inputValue };
                    setTemplateMeta({
                      ...(templateMeta || {}),
                      keyword: [...(templateMeta?.keyword || []), newOption],
                    });
                  }}
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "#f8fafc",
                      borderColor: "#e2e8f0",
                      borderRadius: "0.5rem",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#cbd5e1",
                      },
                    }),
                  }}
                />
              </div>
            </div>

            {/* Thumbnail Asset Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
                Visual Assets
              </h2>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Template Thumbnail
                </label>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {templateThumbnail ? (
                    <div className="relative w-48 h-32 rounded-lg border border-slate-200 overflow-hidden bg-slate-100 shrink-0 shadow-sm">
                      <img
                        src={templateThumbnail}
                        alt="Template thumbnail preview"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <div className="w-48 h-32 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400 shrink-0">
                      <ImageIcon size={28} className="mb-1 text-slate-300" />
                      <span className="text-xs">No thumbnail</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => thumbnailRef.current?.click()}
                      className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                    >
                      <Upload size={14} />
                      {templateThumbnail ? "Replace Image" : "Upload Thumbnail"}
                    </button>
                    <p className="text-[11px] text-slate-400">
                      PNG, JPG, or WebP recommended. Minimum 800x500px resolution.
                    </p>
                  </div>

                  <input
                    ref={thumbnailRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onloadend = () => {
                          setTemplateThumbnail(reader.result);
                        };
                        setTemplateThumbnailFile(file);
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Template JSON Data */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h2 className="text-base font-bold text-slate-900">
                    Template JSON Structure
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Raw component tree and styles schema for the Grigora page builder.
                  </p>
                </div>
              </div>

              <textarea
                rows={12}
                value={templateJSON}
                onChange={(e) => setTemplateJSON(e.target.value)}
                placeholder="Paste JSON structure here..."
                className="w-full font-mono text-xs px-4 py-3 bg-slate-900 text-slate-100 rounded-lg border border-slate-800 focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all resize-y"
              />
            </div>

            {/* Bottom Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Link target="_blank" rel="noopener noreferrer"
                to="/templates"
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isTemplateUpdating}
                className="px-5 py-2.5 text-xs font-semibold text-white bg-[#623ce7] hover:bg-[#5030bc] rounded-lg transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                {isTemplateUpdating ? (
                  <PulseLoader size={6} color="#ffffff" />
                ) : (
                  <>
                    <Save size={14} />
                    {isNew ? "Create Template" : "Save Changes"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Custom Confirmation Modal for Deletion */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-xl border border-slate-200 shadow-2xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Delete Template
              </h3>
              <p className="text-sm text-slate-600">
                Are you sure you want to permanently delete{" "}
                <strong className="text-slate-900 font-semibold">
                  {templateName || templateID}
                </strong>
                ? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isTemplateDeleting}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isTemplateDeleting}
                  className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
                >
                  {isTemplateDeleting ? (
                    <PulseLoader size={6} color="#ffffff" />
                  ) : (
                    <>
                      <Trash2 size={14} />
                      Confirm Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
